import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Radio,
  Plus,
  Search,
  ChevronLeft,
  ChevronRight,
  Eye,
  XCircle,
  Loader2,
  Send,
  Smartphone,
  Users,
  Clock,
  MessageSquare,
  Trash2,
  Save,
  FileText,
  ChevronDown,
  ChevronUp,
  Info,
  Zap,
  AlertCircle,
  CheckCircle2,
  Play,
} from 'lucide-react';
import { apiFetch } from '../lib/api';
import MarkdownTemplateEditor from '../components/admin/MarkdownTemplateEditor';

// ============================================================================
// Types
// ============================================================================

interface BroadcastTask {
  id: number;
  name: string;
  description?: string;
  targetType: string;
  templateContent: string;
  status: 'pending' | 'sending' | 'completed' | 'cancelled';
  totalRecipients: number;
  sentCount: number;
  failedCount: number;
  scheduledAt: string;
  startedAt?: string;
  completedAt?: string;
  hourlyCap: number;
  minIntervalSeconds?: number;
  maxIntervalSeconds?: number;
  createdAt: string;
}

interface BroadcastTemplate {
  id: number;
  name: string;
  content: string;
  isDefault: number;
  createdAt: string;
}

interface PreviewData {
  targetCount: number;
  monthlyDonors: number;
  oneTimeDonors: number;
  sampleRecipients: Array<{ name: string; phone: string; preview: string }>;
  previewMessage: string;
  estimatedDurationMinutes: number;
}

// ============================================================================
// Constants
// ============================================================================

const LIMIT = 20;

const TABS = [
  { key: 'create', label: '創建任務', icon: Plus },
  { key: 'list', label: '任務列表', icon: FileText },
  { key: 'templates', label: '訊息模板', icon: MessageSquare },
] as const;

const statusStyles: Record<string, string> = {
  pending: 'bg-amber-100 text-amber-700 border-amber-200',
  sending: 'bg-blue-100 text-blue-700 border-blue-200',
  completed: 'bg-green-100 text-green-700 border-green-200',
  cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
};

const statusLabels: Record<string, string> = {
  pending: '待發送',
  sending: '發送中',
  completed: '已完成',
  cancelled: '已取消',
};

const targetTypeLabels: Record<string, string> = {
  all: '全部捐贈者',
  monthly: '月捐用戶',
  'one-time': '一次性捐贈者',
  tags: '指定標籤',
};



// ============================================================================
// Components
// ============================================================================

export default function AdminBroadcastTasks() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'create' | 'list' | 'templates'>('create');

  // Task list state
  const [tasks, setTasks] = useState<BroadcastTask[]>([]);
  const [tasksLoading, setTasksLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');

  // Templates state
  const [templates, setTemplates] = useState<BroadcastTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateContent, setNewTemplateContent] = useState('');

  // Create form state
  const [form, setForm] = useState({
    name: '',
    description: '',
    targetType: 'all' as string,
    targetTags: '',
    templateId: '',
    templateContent: '',
    hourlyCap: 100,
    minIntervalSeconds: 30,
    maxIntervalSeconds: 60,
    scheduledAt: '',
  });
  // Preview state
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [showRecipients, setShowRecipients] = useState(false);

  // Test send state
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState<{ success?: boolean; message?: string; error?: string } | null>(null);
  const [testLoading, setTestLoading] = useState(false);

  // Submit state
  const [submitting, setSubmitting] = useState(false);

  // ==========================================================================
  // Data fetching
  // ==========================================================================

  const fetchTasks = useCallback(async () => {
    setTasksLoading(true);
    const query = new URLSearchParams();
    query.set('limit', String(LIMIT));
    query.set('offset', String(offset));
    if (statusFilter) query.set('status', statusFilter);

    const res = await apiFetch<{ tasks: BroadcastTask[]; total: number; limit: number; offset: number }>(
      `/api/admin/broadcast-tasks?${query.toString()}`
    );
    if (res.success && res.data) {
      setTasks(res.data.tasks);
      setTotal(res.data.total);
    }
    setTasksLoading(false);
  }, [offset, statusFilter]);

  const fetchTemplates = useCallback(async () => {
    setTemplatesLoading(true);
    const res = await apiFetch<BroadcastTemplate[]>('/api/admin/broadcast-templates');
    if (res.success && res.data) {
      setTemplates(res.data);
    }
    setTemplatesLoading(false);
  }, []);

  useEffect(() => {
    if (activeTab === 'list') fetchTasks();
    if (activeTab === 'templates') fetchTemplates();
  }, [activeTab, fetchTasks, fetchTemplates]);

  // ==========================================================================
  // Preview
  // ==========================================================================

  const fetchPreview = useCallback(async () => {
    if (!form.templateContent.trim()) return;
    setPreviewLoading(true);
    const res = await apiFetch<PreviewData>('/api/admin/broadcast-tasks?preview=true', {
      method: 'POST',
      body: JSON.stringify({
        targetType: form.targetType,
        targetTags: form.targetTags ? form.targetTags.split(',').map((t) => t.trim()) : [],
        templateContent: form.templateContent,
        minIntervalSeconds: form.minIntervalSeconds,
        maxIntervalSeconds: form.maxIntervalSeconds,
      }),
    });
    if (res.success && res.data) {
      setPreviewData(res.data);
    }
    setPreviewLoading(false);
  }, [form.targetType, form.targetTags, form.templateContent, form.minIntervalSeconds, form.maxIntervalSeconds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (form.templateContent.trim()) {
        fetchPreview();
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [fetchPreview]);

  // ==========================================================================
  // Handlers
  // ==========================================================================

  const handleTemplateSelect = (templateId: string) => {
    const template = templates.find((t) => String(t.id) === templateId);
    if (template) {
      setForm((f) => ({ ...f, templateId, templateContent: template.content }));
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim() || !form.templateContent.trim() || !form.scheduledAt) {
      alert('請填寫所有必填欄位');
      return;
    }
    setSubmitting(true);
    const res = await apiFetch<{ id: number }>('/api/admin/broadcast-tasks', {
      method: 'POST',
      body: JSON.stringify({
        name: form.name,
        description: form.description,
        targetType: form.targetType,
        targetTags: form.targetTags ? form.targetTags.split(',').map((t) => t.trim()) : [],
        templateId: form.templateId ? Number(form.templateId) : null,
        templateContent: form.templateContent,
        hourlyCap: Number(form.hourlyCap),
        minIntervalSeconds: Number(form.minIntervalSeconds),
        maxIntervalSeconds: Number(form.maxIntervalSeconds),
        scheduledAt: new Date(form.scheduledAt).toISOString(),
      }),
    });
    setSubmitting(false);
    if (res.success) {
      setForm({
        name: '',
        description: '',
        targetType: 'all',
        targetTags: '',
        templateId: '',
        templateContent: '',
        hourlyCap: 100,
        minIntervalSeconds: 30,
        maxIntervalSeconds: 60,
        scheduledAt: '',
      });
      setPreviewData(null);
      setActiveTab('list');
    } else {
      alert(res.error?.message || '創建失敗');
    }
  };

  const handleCancel = async (task: BroadcastTask) => {
    if (!confirm(`確定要取消廣播任務「${task.name}」？`)) return;
    const res = await apiFetch(`/api/admin/broadcast-tasks/${task.id}`, {
      method: 'PUT',
      body: JSON.stringify({ status: 'cancelled' }),
    });
    if (res.success) {
      fetchTasks();
    } else {
      alert(res.error?.message || '取消失敗');
    }
  };

  const handleTestSend = async () => {
    if (!testPhone.trim() || !form.templateContent.trim()) {
      alert('請輸入測試電話號碼和訊息內容');
      return;
    }
    setTestLoading(true);
    setTestResult(null);
    const res = await apiFetch<{ sent: boolean; message: string; error: string | null }>('/api/admin/broadcast-test', {
      method: 'POST',
      body: JSON.stringify({
        phone: testPhone.trim(),
        templateContent: form.templateContent,
      }),
    });
    setTestLoading(false);
    if (res.success && res.data) {
      setTestResult({ success: res.data.sent, message: res.data.message, error: res.data.error || undefined });
    } else {
      setTestResult({ success: false, error: res.error?.message || '發送失敗' });
    }
  };

  const handleSaveTemplate = async () => {
    if (!newTemplateName.trim() || !newTemplateContent.trim()) {
      alert('請填寫模板名稱和內容');
      return;
    }
    const res = await apiFetch<{ id: number }>('/api/admin/broadcast-templates', {
      method: 'POST',
      body: JSON.stringify({ name: newTemplateName, content: newTemplateContent }),
    });
    if (res.success) {
      setNewTemplateName('');
      setNewTemplateContent('');
      fetchTemplates();
    } else {
      alert(res.error?.message || '保存失敗');
    }
  };

  const handleDeleteTemplate = async (id: number) => {
    if (!confirm('確定要刪除此模板？')) return;
    const res = await apiFetch(`/api/admin/broadcast-templates/${id}`, { method: 'DELETE' });
    if (res.success) {
      fetchTemplates();
    } else {
      alert(res.error?.message || '刪除失敗');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);
  const currentPage = Math.floor(offset / LIMIT) + 1;

  // ==========================================================================
  // Render helpers
  // ==========================================================================

  const renderCreateTab = () => (
    <form onSubmit={handleCreate} className="space-y-6">
      {/* Template selector */}
      <div className="card p-6 space-y-4">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-brand-600" />
          訊息模板
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">選擇模板</label>
          <select
            value={form.templateId}
            onChange={(e) => handleTemplateSelect(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">-- 選擇預設模板或自訂 --</option>
            {templates.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name} {t.isDefault ? '(預設)' : '(自訂)'}
              </option>
            ))}
          </select>
        </div>

        {/* Message textarea */}
        <div>
          <MarkdownTemplateEditor
            value={form.templateContent}
            onChange={(content) => setForm({ ...form, templateContent: content })}
            variables={[
              { name: 'name', description: '捐款人姓名', example: '陳大文' },
              { name: 'amount', description: '捐款金額', example: '500' },
              { name: 'donation_count', description: '捐款次數', example: '3' },
              { name: 'last_donation_at', description: '上次捐款日期', example: '2026-04-15' },
              { name: 'monthly_gap', description: '距離月捐目標差距', example: '150,000' },
            ]}
            label="訊息內容"
          />
        </div>
      </div>

      {/* Live preview */}
      <AnimatePresence>
        {form.templateContent && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            className="card p-6 space-y-3"
          >
            <h3 className="font-bold text-gray-900 flex items-center gap-2">
              <Play className="w-5 h-5 text-brand-600" />
              即時預覽
            </h3>
            {previewLoading ? (
              <div className="flex items-center gap-2 text-sm text-gray-500">
                <Loader2 className="w-4 h-4 animate-spin" />
                生成預覽中...
              </div>
            ) : previewData ? (
              <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full bg-brand-100 flex items-center justify-center flex-shrink-0">
                    <Smartphone className="w-5 h-5 text-brand-600" />
                  </div>
                  <div className="flex-1">
                    <div className="bg-white rounded-2xl rounded-tl-none p-3 shadow-sm border border-gray-100">
                      <p className="text-sm text-gray-800 leading-relaxed">{previewData.previewMessage}</p>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">使用 {previewData.sampleRecipients[0]?.name || '樣本'} 的資料預覽</p>
                  </div>
                </div>
              </div>
            ) : null}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Target segment */}
      <div className="card p-6 space-y-4">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <Users className="w-5 h-5 text-brand-600" />
          發送對象
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">目標群組</label>
            <select
              value={form.targetType}
              onChange={(e) => setForm({ ...form, targetType: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
            >
              <option value="all">全部捐贈者</option>
              <option value="monthly">月捐用戶</option>
              <option value="one-time">一次性捐贈者</option>
              <option value="tags">指定標籤</option>
            </select>
          </div>
          {form.targetType === 'tags' && (
            <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">標籤（逗號分隔）</label>
              <input
                type="text"
                value={form.targetTags}
                onChange={(e) => setForm({ ...form, targetTags: e.target.value })}
                className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
                placeholder="例如：VIP, 活躍"
              />
            </motion.div>
          )}
        </div>

        {/* Target preview */}
        <AnimatePresence>
          {previewData && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div
                className="flex items-center justify-between bg-brand-50 rounded-xl px-4 py-3 cursor-pointer hover:bg-brand-100 transition-colors"
                onClick={() => setShowRecipients(!showRecipients)}
              >
                <div className="flex items-center gap-2 text-sm text-brand-800">
                  <Users className="w-4 h-4" />
                  <span className="font-medium">預計發送對象: {previewData.targetCount} 人</span>
                  <span className="text-brand-600/70">({previewData.monthlyDonors > 0 ? `月捐: ${previewData.monthlyDonors}人` : ''}{previewData.oneTimeDonors > 0 ? `${previewData.monthlyDonors > 0 ? ', ' : ''}一次性: ${previewData.oneTimeDonors}人` : ''})</span>
                </div>
                {showRecipients ? <ChevronUp className="w-4 h-4 text-brand-600" /> : <ChevronDown className="w-4 h-4 text-brand-600" />}
              </div>
              <AnimatePresence>
                {showRecipients && previewData.sampleRecipients.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    className="overflow-hidden"
                  >
                    <div className="mt-2 bg-white border border-gray-100 rounded-xl divide-y divide-gray-50 max-h-48 overflow-y-auto">
                      {previewData.sampleRecipients.map((r, idx) => (
                        <div key={idx} className="px-4 py-2 text-sm flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <span className="text-gray-500">{r.name}</span>
                            <span className="text-gray-400 text-xs">{r.phone}</span>
                          </div>
                          <span className="text-xs text-gray-400 truncate max-w-[200px]">{r.preview}</span>
                        </div>
                      ))}
                      {previewData.targetCount > previewData.sampleRecipients.length && (
                        <div className="px-4 py-2 text-xs text-gray-400 text-center">
                          還有 {previewData.targetCount - previewData.sampleRecipients.length} 位收件人...
                        </div>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Send protection settings */}
      <div className="card p-6 space-y-5">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <Zap className="w-5 h-5 text-brand-600" />
          發送保護設定
        </h3>

        {/* Interval slider */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700 flex items-center gap-1">
              發送間隔
              <span className="relative group">
                <Info className="w-3.5 h-3.5 text-gray-400 cursor-help" />
                <span className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 hidden group-hover:block w-48 bg-gray-800 text-white text-xs rounded-lg px-3 py-2 z-10">
                  隨機間隔模擬自然發送，降低被封鎖風險
                </span>
              </span>
            </label>
            <span className="text-sm text-brand-700 font-medium">
              {form.minIntervalSeconds} - {form.maxIntervalSeconds} 秒
            </span>
          </div>
          <input
            type="range"
            min={30}
            max={120}
            step={5}
            value={form.minIntervalSeconds}
            onChange={(e) => {
              const val = Number(e.target.value);
              setForm({ ...form, minIntervalSeconds: val, maxIntervalSeconds: val + 30 });
            }}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>30秒</span>
            <span>120秒</span>
          </div>
        </div>

        {/* Hourly cap slider */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="text-sm font-medium text-gray-700">每小時上限</label>
            <span className="text-sm text-brand-700 font-medium">{form.hourlyCap} 則/小時</span>
          </div>
          <input
            type="range"
            min={10}
            max={100}
            step={5}
            value={form.hourlyCap}
            onChange={(e) => setForm({ ...form, hourlyCap: Number(e.target.value) })}
            className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-brand-600"
          />
          <div className="flex justify-between text-xs text-gray-400 mt-1">
            <span>10則</span>
            <span>100則</span>
          </div>
        </div>

        {/* Estimated duration */}
        {previewData && previewData.estimatedDurationMinutes > 0 && (
          <div className="flex items-center gap-2 text-sm text-gray-500 bg-gray-50 rounded-lg px-3 py-2">
            <Clock className="w-4 h-4" />
            預計發送時間：約 {previewData.estimatedDurationMinutes} 分鐘
          </div>
        )}
      </div>

      {/* Test send */}
      <div className="card p-6 space-y-4">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <Smartphone className="w-5 h-5 text-brand-600" />
          發送測試
        </h3>
        <div className="flex gap-3">
          <input
            type="tel"
            value={testPhone}
            onChange={(e) => setTestPhone(e.target.value)}
            placeholder="輸入測試電話號碼（例如：85291234567）"
            className="flex-1 px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
          />
          <button
            type="button"
            onClick={handleTestSend}
            disabled={testLoading}
            className="btn-secondary text-sm py-2 px-4 min-h-[44px] whitespace-nowrap"
          >
            {testLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            發送測試
          </button>
        </div>
        <AnimatePresence>
          {testResult && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              exit={{ opacity: 0, height: 0 }}
              className="overflow-hidden"
            >
              <div className={`rounded-xl p-4 text-sm ${testResult.success ? 'bg-green-50 text-green-800 border border-green-200' : 'bg-red-50 text-red-800 border border-red-200'}`}>
                <div className="flex items-center gap-2 font-medium mb-1">
                  {testResult.success ? <CheckCircle2 className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
                  {testResult.success ? '測試訊息已發送' : '發送失敗'}
                </div>
                {testResult.message && (
                  <p className="text-xs opacity-80 mt-1">實際發送內容：{testResult.message}</p>
                )}
                {testResult.error && (
                  <p className="text-xs opacity-80 mt-1">錯誤：{testResult.error}</p>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Schedule & submit */}
      <div className="card p-6 space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              任務名稱 <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
              placeholder="例如：月捐感謝訊息"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              定時發送 <span className="text-red-500">*</span>
            </label>
            <input
              type="datetime-local"
              value={form.scheduledAt}
              onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
              className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
              required
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">描述</label>
          <input
            type="text"
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
            placeholder="簡短描述此廣播目的"
          />
        </div>
        <div className="flex items-center justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={() => {
              setForm({
                name: '',
                description: '',
                targetType: 'all',
                targetTags: '',
                templateId: '',
                templateContent: '',
                hourlyCap: 100,
                minIntervalSeconds: 30,
                maxIntervalSeconds: 60,
                scheduledAt: '',
              });
              setPreviewData(null);
            }}
            className="btn-secondary text-sm py-2 px-4"
          >
            重置
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn-primary text-sm py-2 px-4 min-h-[44px] disabled:opacity-50"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            創建廣播任務
          </button>
        </div>
      </div>
    </form>
  );

  const renderTaskList = () => (
    <div className="space-y-4">
      {/* Filter */}
      <div className="card p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="搜索任務名稱"
              className="w-full pl-9 pr-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setOffset(0); }}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 bg-white"
          >
            <option value="">全部狀態</option>
            <option value="pending">待發送</option>
            <option value="sending">發送中</option>
            <option value="completed">已完成</option>
            <option value="cancelled">已取消</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-gray-500">
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">任務</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">目標</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">狀態</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">進度</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">排程</th>
                <th className="px-4 py-3 text-left font-medium whitespace-nowrap">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {tasksLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 6 }).map((_, j) => (
                      <td key={j} className="px-4 py-3">
                        <span className="inline-block w-full h-4 bg-gray-100 rounded animate-pulse" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : tasks.length > 0 ? (
                tasks.map((task) => {
                  const progress = task.totalRecipients > 0 ? (task.sentCount / task.totalRecipients) * 100 : 0;
                  return (
                    <tr key={task.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="font-medium text-gray-900">{task.name}</div>
                        {task.description && (
                          <div className="text-xs text-gray-400 mt-0.5">{task.description}</div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                        {targetTypeLabels[task.targetType] || task.targetType}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${statusStyles[task.status] || 'bg-gray-100 text-gray-700 border-gray-200'}`}>
                          {statusLabels[task.status] || task.status}
                        </span>
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap w-48">
                        <div className="flex items-center gap-2">
                          <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                            <motion.div
                              className="h-full bg-brand-500 rounded-full"
                              initial={{ width: 0 }}
                              animate={{ width: `${progress}%` }}
                              transition={{ duration: 0.5 }}
                            />
                          </div>
                          <span className="text-xs text-gray-500 whitespace-nowrap">
                            {task.sentCount}/{task.totalRecipients}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-gray-500 whitespace-nowrap">
                        {new Date(task.scheduledAt).toLocaleString('zh-HK')}
                      </td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => navigate(`/admin/broadcast-tasks/${task.id}`)}
                            className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-gray-100 text-gray-700 text-xs font-medium hover:bg-gray-200 transition-colors"
                            title="查看詳情"
                          >
                            <Eye className="w-3 h-3" />
                            查看
                          </button>
                          {task.status === 'pending' && (
                            <button
                              onClick={() => handleCancel(task)}
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-red-50 text-red-600 text-xs font-medium hover:bg-red-100 transition-colors border border-red-200"
                              title="取消任務"
                            >
                              <XCircle className="w-3 h-3" />
                              取消
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={6} className="px-4 py-8 text-center text-gray-400">
                    沒有符合條件的記錄
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button
            onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
            disabled={offset === 0}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-gray-600 px-3">
            第 {currentPage} / {totalPages} 頁
          </span>
          <button
            onClick={() => setOffset((o) => Math.min((totalPages - 1) * LIMIT, o + LIMIT))}
            disabled={currentPage >= totalPages}
            className="p-2 rounded-lg border border-gray-200 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );

  const renderTemplates = () => (
    <div className="space-y-6">
      {/* Add new template */}
      <div className="card p-6 space-y-4">
        <h3 className="font-bold text-gray-900 flex items-center gap-2">
          <Plus className="w-5 h-5 text-brand-600" />
          新增自定義模板
        </h3>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">模板名稱</label>
          <input
            type="text"
            value={newTemplateName}
            onChange={(e) => setNewTemplateName(e.target.value)}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
            placeholder="例如：生日祝福"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1.5">模板內容</label>
          <textarea
            value={newTemplateContent}
            onChange={(e) => setNewTemplateContent(e.target.value)}
            rows={3}
            className="w-full px-3 py-2.5 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 resize-none"
            placeholder="可使用變量：{{name}} {{total_donated}} {{donation_count}} {{last_donation_date}} {{phone}}"
          />
        </div>
        <div className="flex justify-end">
          <button
            onClick={handleSaveTemplate}
            className="btn-primary text-sm py-2 px-4 min-h-[44px]"
          >
            <Save className="w-4 h-4" />
            保存模板
          </button>
        </div>
      </div>

      {/* Template list */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {templatesLoading ? (
          Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="card p-5 animate-pulse">
              <div className="h-4 bg-gray-100 rounded w-1/3 mb-3" />
              <div className="h-3 bg-gray-100 rounded w-full mb-2" />
              <div className="h-3 bg-gray-100 rounded w-2/3" />
            </div>
          ))
        ) : templates.length > 0 ? (
          templates.map((t) => (
            <motion.div
              key={t.id}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="card p-5 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-gray-900">{t.name}</h4>
                  {t.isDefault === 1 && (
                    <span className="text-[10px] bg-brand-100 text-brand-700 px-2 py-0.5 rounded-full font-medium">
                      預設
                    </span>
                  )}
                </div>
                {t.isDefault !== 1 && (
                  <button
                    onClick={() => handleDeleteTemplate(t.id)}
                    className="text-gray-400 hover:text-red-500 transition-colors p-1"
                    title="刪除模板"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
              <p className="text-sm text-gray-600 leading-relaxed whitespace-pre-wrap">{t.content}</p>
              <button
                type="button"
                onClick={() => {
                  setForm((f) => ({ ...f, templateId: String(t.id), templateContent: t.content }));
                  setActiveTab('create');
                }}
                className="mt-3 inline-flex items-center gap-1 text-xs text-brand-600 font-medium hover:text-brand-700 transition-colors"
              >
                <Plus className="w-3 h-3" />
                使用此模板
              </button>
            </motion.div>
          ))
        ) : (
          <div className="col-span-full text-center text-gray-400 py-8">
            暫無模板
          </div>
        )}
      </div>
    </div>
  );

  return (
    <div className="space-y-6 max-w-6xl">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Radio className="w-6 h-6 text-brand-600" />
          廣播訊息
        </h1>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit">
        {TABS.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                isActive
                  ? 'bg-brand-600 text-white shadow-sm'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-200/50'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.2 }}
        >
          {activeTab === 'create' && renderCreateTab()}
          {activeTab === 'list' && renderTaskList()}
          {activeTab === 'templates' && renderTemplates()}
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
